import React from 'react';
/**
 * Utility functions for formatting dates consistently throughout the application
 */
/**
 * Format a date string according to the user's locale
 * @param dateString - The date string to format
 * @param locale - The locale to use for formatting
 * @param options - Additional Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(dateString: string | Date | null | undefined, locale: string = 'en-US', options: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric'
}): string {
  if (!dateString) return 'N/A';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    // For Australian English, use dd/mm/yyyy format
    if (locale === 'en-AU') {
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    }
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}
/**
 * Format a date with time according to the user's locale
 * @param dateString - The date string to format
 * @param locale - The locale to use for formatting
 * @returns Formatted date and time string
 */
export function formatDateTime(dateString: string | Date | null | undefined, locale: string = 'en-US'): string {
  if (locale === 'en-AU') {
    const dateOptions: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return formatDate(dateString, locale, dateOptions);
  }
  return formatDate(dateString, locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
/**
 * Format a relative time (e.g., "2 days ago")
 * @param dateString - The date string to format
 * @param locale - The locale to use for formatting
 * @returns Relative time string
 */
export function formatRelativeTime(dateString: string | Date | null | undefined, locale: string = 'en-US'): string {
  if (!dateString) return 'N/A';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    // Convert to appropriate unit
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      // Fall back to regular date format for older dates
      return formatDate(date, locale);
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid Date';
  }
}